import { datadogRum } from "@datadog/browser-rum";

import { USE_DATADOG } from "common/utils/datadog";

type CaptureContext = Record<string, unknown>;

/** Safely serialize a value to a JSON string, falling back to a descriptive placeholder on failure. */
const safeStringify = (value: unknown): string => {
  try {
    return JSON.stringify(
      value,
      value instanceof Error ? Object.getOwnPropertyNames(value) : undefined,
    );
  } catch {
    return `[unserializable ${typeof value}]`;
  }
};

/** Convert a CaptureContext into a Record of safely-serialized strings. */
const serializeContext = (extras: CaptureContext): Record<string, string> =>
  Object.fromEntries(Object.entries(extras).map(([key, value]) => [key, safeStringify(value)]));

/**
 * Capture a message across all enabled monitoring platforms (Datadog)
 * and log it to the console.
 *
 * Prefer this over calling datadogRum directly so that all
 * observability providers stay in sync.
 */
export const captureMessage = (message: string, extras?: CaptureContext): void => {
  if (USE_DATADOG) {
    if (extras) {
      datadogRum.addAction(message, serializeContext(extras));
    } else {
      datadogRum.addAction(message);
    }
  }

  console.warn(message);
};

/**
 * Capture an exception across all enabled monitoring platforms (Datadog)
 *
 * Prefer this over calling datadogRum directly so that all
 * observability providers stay in sync.
 */
export const captureException = (error: unknown, extras?: CaptureContext): void => {
  if (USE_DATADOG) {
    if (extras) {
      datadogRum.addError(error as Error, serializeContext(extras));
    } else {
      datadogRum.addError(error as Error);
    }
  }

  console.error(error);
};
